import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = authService.getUser();
    const isAuth = authService.isAuthenticated();
    
    if (isAuth && savedUser) {
      setUserState(savedUser);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const result = await authService.login(credentials);
      
      if (result.success) {
        const { token, user: userData } = result.data;
        
        // Store token and user data
        authService.setToken(token);
        authService.setUser(userData);
        
        // Update state
        setUserState(userData);
        setIsAuthenticated(true);
        
        return { success: true, user: userData };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'An unexpected error occurred during login' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const result = await authService.register(userData);
      return result;
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: 'An unexpected error occurred during registration' 
      };
    }
  };

  const logoutUser = () => {
    authService.logout();
    setUserState(null);
    setIsAuthenticated(false);
  };

  const updateUserProfile = (updatedUser) => {
    authService.setUser(updatedUser);
    setUserState(updatedUser);
  };

  const refreshToken = async () => {
    try {
      const result = await authService.refreshToken();
      if (result.success) {
        const { token, user: userData } = result.data;
        authService.setToken(token);
        authService.setUser(userData);
        setUserState(userData);
        return { success: true };
      } else {
        logoutUser();
        return { success: false, error: result.error };
      }
    } catch (error) {
      logoutUser();
      return { success: false, error: 'Token refresh failed' };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout: logoutUser,
    updateUserProfile,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;